/* eslint-disable camelcase */

import useSnackbar from '@/hooks/snackbars.js';
import {
    getCourseLayersList,
    updateCourseLayer,
    removeCourseLayer,
    restoreCourseLayer,
    uploadCourseLayersTemplate,
    addCourseLayer,
} from '@/api/layersApi.js';
import { createCourseStudentsReport } from '@/api/studentApi.js';
import { getEchoInstance } from '@/services/echo.js';
import router from '@/router/index.js';
import { getErrorMsg } from '@/utils/helpers.js';

const { createSnackbar } = useSnackbar();

export default {
    namespaced: true,
    state: {
        data: null,
        loading: true,
    },
    getters: {
        loading: state => state.loading,
        layersData: state => state.data,
        layers: state => state.data?.data ?? [],
        layersCount: state => state.data?.meta?.total ?? 0,
    },
    mutations: {
        setLayersData(state, data) {
            state.data = data;
        },
        appendLayersData(state, data) {
            state.data = {
                ...data,
                data: [...state.data.data, ...data.data],
            };
        },
        updateLayer(state, layer) {
            state.data.data = state.data.data.map(l => (l.id === layer.id ? layer : l));
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
    },
    actions: {
        resetLayersState({ commit }) {
            commit('setLayersData', null);
            commit('setLoading', true);
        },

        async fetchLayers({ commit }, { courseId, params, withAppend }) {
            if (!courseId) return;

            commit('setLoading', true);

            try {
                const { data } = await getCourseLayersList({
                    courseId,
                    params,
                });

                if (withAppend) commit('appendLayersData', data);
                else commit('setLayersData', data);
            } catch (error) {
                console.error(error);
                await createSnackbar({
                    type: 'error',
                    message: 'Ошибка загрузки. Попробуйте еще раз',
                });
            } finally {
                commit('setLoading', false);
            }
        },

        async renameLayer({ commit }, { courseId, layerId, title }) {
            if (!courseId || !layerId || !title) return;

            try {
                const { data } = await updateCourseLayer({
                    courseId,
                    layerId,
                    layer: {
                        title,
                    },
                });

                commit('updateLayer', data.data);

                return true;
            } catch (e) {
                console.error(e);

                const message = e?.response?.data?.error || 'Ошибка. Попробуйте еще раз';

                await createSnackbar({
                    type: 'error',
                    message,
                });

                return false;
            }
        },

        async removeLayer({ dispatch }, { courseId, layerId }) {
            if (!courseId || !layerId) return;

            try {
                await removeCourseLayer({
                    courseId,
                    layerId,
                });

                await dispatch('fetchLayers', {
                    courseId,
                    params: router.currentRoute.value.query,
                });
            } catch (error) {
                console.error(error);
                await createSnackbar({
                    type: 'error',
                    message: 'Ошибка удаления. Попробуйте еще раз',
                });
            }
        },

        async restoreLayer({ dispatch }, { courseId, layerId }) {
            if (!courseId || !layerId) return;

            try {
                await restoreCourseLayer({
                    courseId,
                    layerId,
                });

                await dispatch('fetchLayers', {
                    courseId,
                    params: router.currentRoute.value.query,
                });
            } catch (error) {
                console.error(error);
                await createSnackbar({
                    type: 'error',
                    message: 'Ошибка восстановления. Попробуйте еще раз',
                });
            }
        },

        async addLayer(_, { courseId, groupsCount } = { groupsCount: 0 }) {
            if (!courseId) return;

            try {
                await addCourseLayer({
                    courseId,
                    groupsCount,
                });
            } catch (error) {
                console.error(error);
                await createSnackbar({
                    type: 'error',
                    message: 'Не удалось создать слой. Попробуйте еще раз',
                });
            }
        },

        async uploadLayersTemplate(_, { courseId, file } = {}) {
            if (!courseId || !file) return;

            try {
                await uploadCourseLayersTemplate({
                    courseId,
                    file,
                });

                createSnackbar({
                    type: 'success',
                    message: 'Импорт добавлен в очередь',
                });
            } catch (error) {
                console.error(error);
                await createSnackbar({
                    type: 'error',
                    message: 'Не удалось загрузить шаблон слоев. Попробуйте еще раз',
                });
            }
        },

        async createLayersReport(_, { courseId, developmentCenterId }) {
            try {
                const { data } = await createCourseStudentsReport({ courseId });

                if (data.success === false) {
                    throw data;
                }

                createSnackbar({
                    type: 'info',
                    size: 'large',
                    title: 'Отчет добавлен в очередь на обработку',
                    message: 'По завершению обработки, отчет можно скачать на странице отчетов',
                    link: {
                        text: 'Перейти к отчетам',
                        route: {
                            name: 'AdminReports',
                            params: {
                                developmentCenterId,
                            },
                        },
                    },
                });
            } catch (e) {
                console.error(e);
                createSnackbar({
                    type: 'error',
                    size: 'large',
                    title: getErrorMsg(e, 'Ошибка импорта. Попробуйте еще раз'),
                });
            }
        },

        // eslint-disable-next-line max-lines-per-function
        initImportChannel: ({ dispatch }, { userId }) => {
            getEchoInstance()
                .private(`users.${userId}.student`)
                .stopListening('.group.imported')
                .listen('.group.imported', importData => {
                    const { imported_rows_count, total_rows_count, location } = importData;
                    const failedCount = total_rows_count - imported_rows_count;
                    const snackbarTitle = imported_rows_count
                        ? `Успешно импортировано ${imported_rows_count} из ${total_rows_count}`
                        : `${imported_rows_count} из ${total_rows_count} импортировано`;

                    createSnackbar({
                        type: 'info',
                        size: 'large',
                        title: snackbarTitle,
                        message: failedCount
                            ? `По ${failedCount} строкам не заполнены логины или все ячейки с названиями слоев для одного логина остались незаполненными. Проверьте правильность заполнения`
                            : '',
                        link: {
                            text: 'Перейти к списку слоев',
                            route: {
                                name: 'VersionLayersMainView',
                                params: {
                                    developmentCenterId: location.development_center_id,
                                    solutionId: location.project_id,
                                    versionId: location.course_id,
                                },
                            },
                        },
                    });


                    if (location.course_id.toString() !== router.currentRoute.value.params.versionId) {
                        return;
                    }

                    if (router.currentRoute.value.name === 'VersionLayerView' && router.currentRoute.value.params.layerId) {
                        dispatch('layer/resetFilters', {}, { root: true });
                        dispatch('layer/loadLayer', {
                            courseId: location.course_id,
                            layerId: router.currentRoute.value.params.layerId,
                            page: 1,
                        }, { root: true });
                    }

                    if (router.currentRoute.value.name === 'VersionLayersMainView') {
                        router.replace({
                            query: {},
                        });
                        dispatch('fetchLayers', {
                            courseId: location.course_id,
                            params: router.currentRoute.value.query,
                        });
                    }
                })
                .stopListening('.group.import_failed')
                .listen('.group.import_failed', importData => {
                    const { location } = importData;

                    createSnackbar({
                        type: 'error',
                        size: 'large',
                        title: 'Ошибка импорта. Попробуйте еще раз',
                        link: {
                            text: 'Перейти к списку слоев',
                            route: {
                                name: 'VersionLayersMainView',
                                params: {
                                    developmentCenterId: location.development_center_id,
                                    solutionId: location.project_id,
                                    versionId: location.course_id,
                                },
                            },
                        },
                    });
                });
        },
    },
};
